// DIRECTIVE-040 commit 1: Florida delivery-zone fee chart.
//
// 7 tiers cover all 67 FL counties. Zone numbers are the brand-facing
// labels (Zone 1 = nearest = cheapest); fees are flat per-order
// regardless of box count or distance within the zone. Out-of-state
// orders are pickup-only at the Doral pickup location + carry $0 in portal
// math (Zone 0 / "pickup").
//
// The county tier mapping is locked Jun 17 in DIRECTIVE-040 § 1. Any
// change here must be paired with a sweep of fl-zip-to-zone.json
// (built by scripts/delivery/build-fl-zip-to-zone.ts).

export type ZoneNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ZoneSpec = {
  zone: ZoneNumber;
  fee: number;
  label: string;
  counties: readonly string[];
};

// Canonical county names match the Census ZCTA->County relationship
// file's NAME20 column (without the trailing " County" suffix). Build
// script normalizes whatever it pulls from Census against this list.
export const FL_ZONES: readonly ZoneSpec[] = [
  {
    zone: 1,
    fee: 25,
    label: "Miami-Dade / Broward / S. Palm Beach",
    counties: ["Miami-Dade", "Broward", "Palm Beach"],
  },
  {
    zone: 2,
    fee: 39,
    label: "Northern Palm Beach / Martin",
    // Palm Beach is split between Zone 1 (southern) and Zone 2
    // (northern, north of WPB). The split is ZIP-based, not
    // county-based, so the build script overrides individual ZIPs in
    // Palm Beach county to Zone 2 where appropriate. See PALM_BEACH_
    // NORTH_ZIPS below.
    counties: ["Martin"],
  },
  {
    zone: 3,
    fee: 49,
    label: "Treasure Coast",
    counties: ["St. Lucie", "Indian River"],
  },
  {
    zone: 4,
    fee: 75,
    label: "SW FL / Keys / Central FL",
    counties: [
      "Collier",
      "Lee",
      "Monroe",
      "Orange",
      "Osceola",
      "Seminole",
      "Lake",
    ],
  },
  {
    zone: 5,
    fee: 85,
    label: "Tampa Bay / Central / Space Coast / Interior",
    counties: [
      "Hillsborough",
      "Pinellas",
      "Pasco",
      "Hernando",
      "Polk",
      "Manatee",
      "Sarasota",
      "Charlotte",
      "Sumter",
      "Volusia",
      "Brevard",
      // Q7 lock Jun 17 (Alexis): blanket Zone 5 for the 6 interior
      // counties absent from the original Jun 17 tier chart.
      // Defensible drive economics (2-3.5h from Doral) + simpler
      // chart for marketing. Low volume markets so the tier choice
      // has minimal portfolio impact.
      "DeSoto",
      "Glades",
      "Hardee",
      "Hendry",
      "Highlands",
      "Okeechobee",
    ],
  },
  {
    zone: 6,
    fee: 149,
    label: "North-Central FL / Jacksonville",
    counties: [
      "Marion",
      "Alachua",
      "Citrus",
      "Levy",
      "Putnam",
      "Flagler",
      "Duval",
      "St. Johns",
      "Clay",
      "Nassau",
      "Columbia",
      "Baker",
      "Union",
      "Bradford",
      "Suwannee",
      "Hamilton",
      "Lafayette",
      "Dixie",
      "Gilchrist",
      "Taylor",
    ],
  },
  {
    zone: 7,
    fee: 189,
    label: "Panhandle",
    counties: [
      "Leon",
      "Bay",
      "Escambia",
      "Santa Rosa",
      "Okaloosa",
      "Walton",
      "Gulf",
      "Franklin",
      "Wakulla",
      "Jefferson",
      "Madison",
      "Gadsden",
      "Liberty",
      "Calhoun",
      "Jackson",
      "Holmes",
      "Washington",
    ],
  },
] as const;

// Palm Beach county is split: most ZIPs are Zone 1 ($25), but ZIPs
// north of West Palm Beach fall into Zone 2 ($39). Source: directive
// table § 1 "Northern Palm Beach (north of WPB)" — Jupiter, Tequesta,
// Palm Beach Gardens. These ZIPs override the county-based mapping
// in the build script.
//
// The list below was compiled from the city list in the directive and
// USPS ZIP-by-city lookup. Update by running scripts/delivery/audit-
// palm-beach-split.ts if the brand boundary changes.
export const PALM_BEACH_NORTH_ZIPS: readonly string[] = [
  // Jupiter
  "33458",
  "33468",
  "33469",
  "33477",
  "33478",
  // Tequesta
  "33455",
  "33469",
  // Palm Beach Gardens
  "33403",
  "33408",
  "33410",
  "33418",
  // Juno Beach
  "33408",
  // North Palm Beach
  "33408",
  "33410",
] as const;

// Pickup-only fallback for any ZIP not in the FL set (out-of-state +
// the ~2-3% PO-box-only FL ZIPs the Census ZCTA file doesn't cover).
export const PICKUP_ZONE: ZoneSpec = {
  zone: 0,
  fee: 0,
  label: "Doral pickup",
  counties: [],
};

const COUNTY_TO_ZONE = new Map<string, ZoneSpec>();
for (const z of FL_ZONES) {
  for (const c of z.counties) COUNTY_TO_ZONE.set(c, z);
}

/**
 * Resolve a normalized FL county name to its zone. Returns null when
 * the county is not in the locked tier table — the caller should
 * treat that as a data-integrity bug (lint test fails CI).
 */
export function zoneForCounty(county: string): ZoneSpec | null {
  return COUNTY_TO_ZONE.get(county) ?? null;
}

/**
 * Canonical list of every FL county name used by FL_ZONES. Used by
 * the build script to validate Census-derived data + by lint tests
 * to assert no county is silently dropped.
 */
export function allCountiesInZones(): readonly string[] {
  const out: string[] = [];
  for (const z of FL_ZONES) out.push(...z.counties);
  return out;
}
